import { useState, useMemo } from 'react';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Check,
  Plus,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  useTimeEntries,
  useCreateTimeEntry,
  useApproveTimeEntry,
  useProjects,
} from '@/hooks/use-projects';
import { CostCodeSelector } from '@/app/components/cost-code-selector';
import { PersonSearchComboBox } from '@/components/shared/person-search-combo-box';
import type { PersonOption } from '@/components/shared/person-search-combo-box';
import { usePlatformModules } from '@/hooks/use-modules';
import { handleApiError } from '@/lib/api-client';
import type { ApiError } from '@/lib/api-client';
import type { CreateTimeEntryPayload } from '@/lib/types';
import { formatDateOnly, formatLocalDate } from '@/lib/date-utils';
import { useOrgToday } from '@/hooks/use-settings';
import { ApprovalError, useSelfApproval } from '@/app/components/payroll/self-approval';

type ViewMode = 'my' | 'approve';

function getWeekDates(offset: number, todayValue: string): Date[] {
  const today = new Date(`${todayValue}T00:00:00`);
  const day = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function formatShortDay(d: Date): string {
  return formatDateOnly(formatLocalDate(d), '—', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function TimeEntryPage() {
  const orgToday = useOrgToday();
  const [viewMode, setViewMode] = useState<ViewMode>('my');
  const [weekOffset, setWeekOffset] = useState(0);
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const { isModuleEnabled } = usePlatformModules();
  const payrollEnabled = isModuleEnabled('payroll');
  const crewEnabled = isModuleEnabled('crew');

  const weekDates = useMemo(() => getWeekDates(weekOffset, orgToday), [orgToday, weekOffset]);
  const weekStart = formatLocalDate(weekDates[0]);
  const weekEnd = formatLocalDate(weekDates[6]);

  const { data: entries, isLoading } = useTimeEntries({
    date_from: weekStart,
    date_to: weekEnd,
    page_size: 200,
  });

  const { data: allProjects } = useProjects({ status: 'active', page_size: 100 });
  const createEntry = useCreateTimeEntry();
  const approveEntry = useApproveTimeEntry();
  const selfApproval = useSelfApproval('time entry');
  const approve = (id: number) =>
    void selfApproval.approve(id, (confirmSelfApproval) =>
      approveEntry.mutateAsync({ id, confirmSelfApproval }),
    );

  const [quickForm, setQuickForm] = useState({
    project: '',
    date: orgToday,
    hours: '',
    description: '',
    billable: true,
    cost_code: null as number | null,
  });
  const [selectedTech, setSelectedTech] = useState<PersonOption | null>(null);
  const [quickErrors, setQuickErrors] = useState<ApiError | null>(null);

  const getFieldError = (field: string) => quickErrors?.errors?.[field]?.join(' ');

  // Group entries by project for weekly view
  const entriesByProject = useMemo(() => {
    if (!entries?.results) return new Map<string, Map<string, number>>();
    const map = new Map<string, Map<string, number>>();
    for (const e of entries.results) {
      const projKey = e.project_name || `Project ${e.project}`;
      if (!map.has(projKey)) map.set(projKey, new Map());
      const dayMap = map.get(projKey)!;
      const current = dayMap.get(e.date) ?? 0;
      dayMap.set(e.date, current + parseFloat(e.hours));
    }
    return map;
  }, [entries]);

  // Total hours per day
  const dailyTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const d of weekDates) {
      const dateStr = formatLocalDate(d);
      let total = 0;
      for (const dayMap of entriesByProject.values()) {
        total += dayMap.get(dateStr) ?? 0;
      }
      totals.set(dateStr, total);
    }
    return totals;
  }, [entriesByProject, weekDates]);

  const weekTotal = Array.from(dailyTotals.values()).reduce((sum, v) => sum + v, 0);

  const workerSelected = Boolean(
    selectedTech?.person_id ||
    (crewEnabled && selectedTech?.tech_id)
  );

  const handleQuickEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    setQuickErrors(null);

    if (!quickForm.project || !workerSelected || !quickForm.hours) {
      setQuickErrors({
        message: 'Project, worker, and hours are required.',
        status: 0,
        errors: {
          ...(quickForm.project ? {} : { project: ['This field is required.'] }),
          ...(workerSelected ? {} : { worker: ['This field is required.'] }),
          ...(quickForm.hours ? {} : { hours: ['This field is required.'] }),
        },
      });
      return;
    }

    try {
      const payload: CreateTimeEntryPayload = {
        project: parseInt(quickForm.project),
        date: quickForm.date,
        hours: quickForm.hours,
        description: quickForm.description,
        billable: quickForm.billable,
        cost_code: quickForm.cost_code,
      };

      if (crewEnabled && selectedTech?.tech_id) {
        payload.tech = selectedTech.tech_id;
      }

      if (selectedTech?.person_id) {
        payload.person = selectedTech.person_id;
      }

      await createEntry.mutateAsync(payload);
      setShowQuickEntry(false);
      setQuickForm({
        project: '',
        date: orgToday,
        hours: '',
        description: '',
        billable: true,
        cost_code: null,
      });
      setSelectedTech(null);
    } catch (err) {
      setQuickErrors(handleApiError(err));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Clock className="h-7 w-7 text-green-600" />
            <h1 className="text-2xl font-bold text-gray-900">Time Tracking</h1>
          </div>
          <p className="text-gray-600 mt-1">Log and manage time entries</p>
        </div>
        <div className="flex gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'my' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('my')}
              className="rounded-none"
            >
              My Timesheets
            </Button>
            <Button
              variant={viewMode === 'approve' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('approve')}
              className="rounded-none"
            >
              Approve Time
            </Button>
          </div>
          <Dialog open={showQuickEntry} onOpenChange={(open) => { setShowQuickEntry(open); if (open) setQuickErrors(null); }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Log Time
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Quick Time Entry</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleQuickEntry} className="space-y-4">
                {quickErrors && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
                    {quickErrors.message}
                  </div>
                )}
                <div>
                  <Label>Project *</Label>
                  <Select
                    value={quickForm.project}
                    onValueChange={(val) =>
                      setQuickForm((p) => ({ ...p, project: val, cost_code: null }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {allProjects?.results.map((proj) => (
                        <SelectItem key={proj.id} value={proj.id.toString()}>
                          {proj.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {getFieldError('project') && (
                    <p className="text-red-600 text-xs mt-1">{getFieldError('project')}</p>
                  )}
                </div>
                <div>
                  <Label>{crewEnabled ? 'Technician' : 'Worker'} *</Label>
                  <PersonSearchComboBox
                    value={selectedTech}
                    onChange={(person) => setSelectedTech(person)}
                    placeholder={crewEnabled ? 'Search technician...' : 'Search worker...'}
                    endpoint={crewEnabled ? 'techs' : 'persons'}
                  />
                  {getFieldError('worker') && (
                    <p className="text-red-600 text-xs mt-1">{getFieldError('worker')}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={quickForm.date}
                      onChange={(e) =>
                        setQuickForm((p) => ({ ...p, date: e.target.value }))
                      }
                      required
                    />
                    {getFieldError('date') && (
                      <p className="text-red-600 text-xs mt-1">{getFieldError('date')}</p>
                    )}
                  </div>
                  <div>
                    <Label>Hours *</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      max="24"
                      value={quickForm.hours}
                      onChange={(e) =>
                        setQuickForm((p) => ({ ...p, hours: e.target.value }))
                      }
                      placeholder="0.0"
                      required
                    />
                    {getFieldError('hours') && (
                      <p className="text-red-600 text-xs mt-1">{getFieldError('hours')}</p>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={quickForm.description}
                    onChange={(e) =>
                      setQuickForm((p) => ({ ...p, description: e.target.value }))
                    }
                    rows={2}
                  />
                </div>
                {quickForm.project && (
                  <div>
                    <Label>Cost Code</Label>
                    <CostCodeSelector
                      projectId={parseInt(quickForm.project)}
                      value={quickForm.cost_code}
                      onChange={(id) =>
                        setQuickForm((p) => ({ ...p, cost_code: id }))
                      }
                      enabled={payrollEnabled}
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="billable"
                    checked={quickForm.billable}
                    onChange={(e) =>
                      setQuickForm((p) => ({ ...p, billable: e.target.checked }))
                    }
                  />
                  <Label htmlFor="billable">Billable</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowQuickEntry(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createEntry.isPending}>
                    {createEntry.isPending ? 'Saving...' : 'Log Time'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Week Navigator */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setWeekOffset((w) => w - 1)}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <div className="text-center">
          <p className="font-medium">
            {formatDateOnly(weekStart, '—', {
              month: 'long',
              day: 'numeric',
            })}{' '}
            –{' '}
            {formatDateOnly(weekEnd, '—', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
          <p className="text-sm text-gray-500">
            Total: {weekTotal.toFixed(1)} hours
          </p>
        </div>
        <div className="flex gap-2">
          {weekOffset !== 0 && (
            <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)}>
              This Week
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setWeekOffset((w) => w + 1)}>
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {viewMode === 'my' ? (
        /* Weekly Timesheet Grid */
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50 text-center text-gray-500">
                        <th className="p-3 text-left font-medium min-w-[200px]">Project</th>
                        {weekDates.map((d) => (
                          <th key={formatLocalDate(d)} className="p-3 font-medium min-w-[80px]">
                            {formatShortDay(d)}
                          </th>
                        ))}
                        <th className="p-3 font-medium min-w-[80px]">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {Array.from(entriesByProject.entries()).map(([projectName, dayMap]) => {
                        const rowTotal = weekDates.reduce(
                          (sum, d) => sum + (dayMap.get(formatLocalDate(d)) ?? 0),
                          0
                        );
                        return (
                          <tr key={projectName} className="hover:bg-gray-50">
                            <td className="p-3 font-medium">{projectName}</td>
                            {weekDates.map((d) => {
                              const dateStr = formatLocalDate(d);
                              const hours = dayMap.get(dateStr);
                              return (
                                <td key={dateStr} className="p-3 text-center">
                                  {hours ? (
                                    <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">
                                      {hours.toFixed(1)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="p-3 text-center font-semibold">{rowTotal.toFixed(1)}</td>
                          </tr>
                        );
                      })}
                      {entriesByProject.size === 0 && (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-gray-500">
                            No time entries for this week
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-gray-50 font-semibold">
                        <td className="p-3">Daily Totals</td>
                        {weekDates.map((d) => (
                          <td key={formatLocalDate(d)} className="p-3 text-center">
                            {(dailyTotals.get(formatLocalDate(d)) ?? 0).toFixed(1)}
                          </td>
                        ))}
                        <td className="p-3 text-center">{weekTotal.toFixed(1)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Mobile card list */}
                <div className="lg:hidden space-y-4 p-4">
                  {entriesByProject.size === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No time entries for this week
                    </div>
                  ) : (
                    Array.from(entriesByProject.entries()).map(([projectName, dayMap]) => {
                      const rowTotal = weekDates.reduce(
                        (sum, d) => sum + (dayMap.get(formatLocalDate(d)) ?? 0),
                        0
                      );
                      return (
                        <Card key={projectName}>
                          <CardContent className="p-4">
                            <p className="font-medium text-gray-900 mb-3">{projectName}</p>
                            <div className="grid grid-cols-2 gap-2">
                              {weekDates.map((d) => {
                                const dateStr = formatLocalDate(d);
                                const hours = dayMap.get(dateStr);
                                return (
                                  <div key={dateStr} className="contents">
                                    <span className="text-sm text-gray-500 py-1">{formatShortDay(d)}</span>
                                    <span className="text-sm text-right py-1">
                                      {hours ? (
                                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">
                                          {hours.toFixed(1)}
                                        </span>
                                      ) : (
                                        <span className="text-gray-300">—</span>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="flex justify-between items-center mt-3 pt-3 border-t">
                              <span className="text-sm font-semibold text-gray-700">Total</span>
                              <span className="text-sm font-semibold">{rowTotal.toFixed(1)}h</span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}

                  {/* Daily totals summary */}
                  {entriesByProject.size > 0 && (
                    <Card className="bg-gray-50">
                      <CardContent className="p-4">
                        <p className="font-semibold text-gray-700 mb-2">Daily Totals</p>
                        <div className="grid grid-cols-2 gap-2">
                          {weekDates.map((d) => (
                            <div key={formatLocalDate(d)} className="contents">
                              <span className="text-sm text-gray-500">{formatShortDay(d)}</span>
                              <span className="text-sm text-right font-medium">
                                {(dailyTotals.get(formatLocalDate(d)) ?? 0).toFixed(1)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between items-center mt-3 pt-3 border-t">
                          <span className="text-sm font-semibold text-gray-700">Week Total</span>
                          <span className="text-sm font-semibold">{weekTotal.toFixed(1)}h</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Approve Time View */
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pending Approval</CardTitle>
          </CardHeader>
          <CardContent>
            {entries && entries.results.filter((e) => !e.approved).length > 0 ? (
              <>
                {/* Desktop table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50 text-left text-gray-500">
                        <th className="p-3 font-medium">Date</th>
                        <th className="p-3 font-medium">Tech</th>
                        <th className="p-3 font-medium">Project</th>
                        <th className="p-3 font-medium">Hours</th>
                        <th className="p-3 font-medium">Cost Code</th>
                        <th className="p-3 font-medium">Description</th>
                        <th className="p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {entries.results
                        .filter((e) => !e.approved)
                        .map((entry) => (
                          <tr key={entry.id} className="hover:bg-gray-50">
                            <td className="p-3">{formatDateOnly(entry.date)}</td>
                            <td className="p-3">{entry.tech_name}</td>
                            <td className="p-3">{entry.project_name}</td>
                            <td className="p-3 font-medium">{parseFloat(entry.hours).toFixed(1)}</td>
                            <td className="p-3 text-gray-600">
                              {entry.cost_code_detail
                                ? `${entry.cost_code_detail.code} — ${entry.cost_code_detail.name}`
                                : '—'}
                            </td>
                            <td className="p-3 text-gray-600 max-w-xs truncate">
                              {entry.description || '—'}
                            </td>
                            <td className="p-3">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => approve(entry.id)}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <ApprovalError message={selfApproval.errorMessage} className="my-2" />
                {selfApproval.dialog}
                {/* Mobile card list */}
                <div className="lg:hidden space-y-3">
                  {entries.results
                    .filter((e) => !e.approved)
                    .map((entry) => (
                      <Card key={entry.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900">{entry.tech_name}</p>
                              <p className="text-xs text-gray-500">{entry.project_name}</p>
                            </div>
                            <span className="text-sm font-semibold flex-shrink-0">{parseFloat(entry.hours).toFixed(1)}h</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-xs text-gray-500 min-w-0">
                              <span>{formatDateOnly(entry.date)}</span>
                              {entry.description && (
                                <span className="ml-2 truncate">{entry.description}</span>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="min-h-[44px] flex-shrink-0"
                              onClick={() => approve(entry.id)}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No entries pending approval this week
              </p>
            )}

            {/* Already approved */}
            {entries && entries.results.filter((e) => e.approved).length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-3">Approved</h4>
                <div className="space-y-2">
                  {entries.results
                    .filter((e) => e.approved)
                    .map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between p-2 bg-green-50 rounded text-sm"
                      >
                        <div className="flex items-center gap-4">
                          <Badge className="bg-green-100 text-green-800">Approved</Badge>
                          <span>{formatDateOnly(entry.date)}</span>
                          <span className="font-medium">{entry.tech_name}</span>
                          <span className="text-gray-600">{entry.project_name}</span>
                        </div>
                        <span className="font-medium">{parseFloat(entry.hours).toFixed(1)}h</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
